import { nativeBridgePresent } from "@/platform/nativeBridge";

const FALLBACK_NAME = "export.xlsx";

/** One path segment. Native write uses this name under the cache `exports/` folder. */
export function downloadFileName(name: string): string {
  const segment = name.replace(/\\/g, "/").split("/").pop()?.trim() ?? "";
  const cleaned = segment.replace(/[\u0000-\u001f\u007f]/g, "");
  if (!cleaned || cleaned === "." || cleaned === "..") return FALLBACK_NAME;
  return cleaned;
}

/**
 * iOS share parses the file URI with `URL(string:)`. Spaces, commas, and
 * combining marks in the path make that parse fail and the file is dropped.
 */
export function nativeSafeFileName(name: string): string {
  const segment = downloadFileName(name);
  const dot = segment.lastIndexOf(".");
  const ext = dot > 0 ? segment.slice(dot + 1).replace(/[^A-Za-z0-9]/g, "").slice(0, 8) : "";
  const stemSource = dot > 0 ? segment.slice(0, dot) : segment;
  const stem = stemSource
    .replace(/đ/g, "d")
    .replace(/Đ/g, "D")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^A-Za-z0-9._-]+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
  const base = stem || "download";
  return ext ? `${base}.${ext.toLowerCase()}` : base;
}

function saveBlobInBrowser(blob: Blob, fileName: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 1500);
}

/**
 * Browser: object-URL download. Native shell: write the blob and open the system share sheet.
 */
export async function saveBlobAsFile(blob: Blob, fileName: string): Promise<void> {
  const safeName = downloadFileName(fileName);
  if (!nativeBridgePresent()) {
    saveBlobInBrowser(blob, safeName);
    return;
  }
  const { saveBlobNative } = await import("@/platform/capacitor/saveBlobNative");
  await saveBlobNative(blob, nativeSafeFileName(safeName));
}
